import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';

// ── Audit, the general meeting, DBD filing, and who keeps the books ─────────────────────────────
// The deadlines here are computed from the client's OWN year end and its own meeting date, not from
// a fixed 31 May: Accounting Act s.11 gives a partnership five months from the closing date and a
// company one month from the meeting that approved the statements, and the system used to apply one
// hard-coded date to both.
//
// Where an answer cannot be established it says UNKNOWN in amber rather than showing a pass. An
// audit exemption granted by accident is worse than a filing made unnecessarily.

type Deadlines = {
  periodEnd: string; agmDueBy: string; agmDate: string; statementsToShareholdersBy: string;
  filingDueBy: string; filingBasis: string; shareholderListDueBy: string;
  filed: { kind: string; filedOn: string; receiptNo: string }[];
  audit: { required: boolean | null; reason: string; tests: { label: string; value: number | null; limit: number; exceeds: boolean | null }[]; source: string };
  overdue: string[]; notes: string[];
};
type Appointment = { id: string; name: string; tfac_no: string; tfac_status: string; qualification: string;
  from_date: string; to_date: string; notified_dbd_on: string; ended_notified_on: string };

const dmy = (iso: string) => { const p = String(iso || '').split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso; };
const baht = (s: number | null) => s === null ? '—' : (Number(s) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const KIND_TH: Record<string, string> = { financial_statements: 'งบการเงิน', shareholder_list: 'บอจ.5' };

const btn: React.CSSProperties = {
  padding: '6px 11px', fontSize: 12, fontWeight: 700, borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', cursor: 'pointer',
};
const inp: React.CSSProperties = {
  padding: '6px 9px', fontSize: 12.5, borderRadius: 7, border: '1px solid var(--border-strong)',
  background: 'var(--bg-surface)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box',
};
const row: React.CSSProperties = { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 9 };

export default function CompliancePanel({ clientId, periodId }: { clientId: string; periodId: string }) {
  const { t } = useTranslation();
  const [d, setD] = useState<Deadlines | null>(null);
  const [appts, setAppts] = useState<Appointment[] | null>(null);
  const [review, setReview] = useState<any>(null);
  const [mode, setMode] = useState<'' | 'audit' | 'file'>('');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const [af, setAf] = useState({ agmDate: '', statementsSentOn: '', auditCompletedOn: '', auditOpinion: '',
    auditorName: '', auditorLicense: '', auditorKind: '', auditFee: '' });
  const [fl, setFl] = useState({ kind: 'financial_statements', filedOn: '', receiptNo: '' });

  const load = useCallback(async () => {
    try {
      const [x, a, r]: any[] = await Promise.all([
        api.acDeadlines(clientId, periodId), api.acAppointments(clientId), api.acBackingReview(clientId, periodId),
      ]);
      setD(x); setAppts(a.appointments || []); setReview(r);
      setAf(v => ({ ...v, agmDate: x.agmDate || '' }));
    } catch (e: any) { setErr(e.message); }
  }, [clientId, periodId]);
  useEffect(() => { load(); }, [load]);

  const saveAudit = async () => {
    setBusy('audit'); setErr(''); setMsg('');
    try { await api.acSetAuditFacts(clientId, periodId, { ...af, auditFee: af.auditFee ? Number(af.auditFee) : undefined }); setMode(''); await load(); }
    catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };
  const file = async () => {
    setBusy('file'); setErr(''); setMsg('');
    try { await api.acRecordFiling(clientId, periodId, fl); setMsg(t('pa_compliance_filed_msg', { kind: KIND_TH[fl.kind], date: dmy(fl.filedOn) })); setFl({ ...fl, filedOn: '', receiptNo: '' }); setMode(''); await load(); }
    catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };
  const notify = async (a: Appointment, which: 'start' | 'end') => {
    setBusy(a.id); setErr('');
    try { await api.acNotified(a.id, which, new Date().toISOString().slice(0, 10)); await load(); }
    catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  if (!d) return null;
  const live = (appts || []).filter(a => !a.to_date);
  const auditColour = d.audit.required === null ? '#B45309' : d.audit.required ? 'var(--text-primary)' : '#166534';

  return (
    <div style={{ border: `1px solid ${d.overdue.length ? '#F3B9B9' : 'var(--border)'}`, borderRadius: 10, background: 'var(--bg-surface)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 13px', flexWrap: 'wrap' }}>
        <i className="ti ti-gavel" style={{ fontSize: 18, color: d.overdue.length ? '#B42318' : 'var(--accent)' }} aria-hidden />
        <span style={{ minWidth: 0, flex: 1 }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{t('pa_compliance_title')}</span>
          <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)' }}>
            {d.filingDueBy ? t('pa_compliance_file_by', { date: dmy(d.filingDueBy) }) : t('pa_compliance_due_unknown')} · {d.filingBasis}
          </span>
        </span>
        <button onClick={() => setMode(m => m === 'audit' ? '' : 'audit')} style={btn}><i className="ti ti-edit" aria-hidden /> {t('pa_compliance_record_meeting_audit')}</button>
        <button onClick={() => setMode(m => m === 'file' ? '' : 'file')} style={btn}><i className="ti ti-upload" aria-hidden /> {t('pa_compliance_record_filing')}</button>
      </div>

      {/* Whether an audit is needed at all, with the three tests and their limits on show. */}
      <div style={{ borderTop: '1px solid var(--border)', padding: '9px 13px', fontSize: 12, lineHeight: 1.8 }}>
        <span style={{ fontWeight: 700, color: auditColour }}>
          {d.audit.required === null ? t('pa_compliance_audit_unknown') : d.audit.required ? t('pa_compliance_audit_required') : t('pa_compliance_audit_exempt')}
        </span>
        <span style={{ color: 'var(--text-secondary)' }}> — {d.audit.reason}</span>
        {!!d.audit.tests.length && (
          <div style={{ marginTop: 5, display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 11.5, color: 'var(--text-tertiary)' }}>
            {d.audit.tests.map(t => (
              <span key={t.label} style={{ color: t.exceeds ? '#B42318' : t.exceeds === null ? '#B45309' : 'var(--text-tertiary)' }}>
                {t.label}: {baht(t.value)} / {t.limit.toLocaleString()}
              </span>
            ))}
          </div>
        )}
        <div style={{ marginTop: 4, fontSize: 10.5, color: 'var(--text-tertiary)' }}>{d.audit.source}</div>
      </div>

      {!!d.overdue.length && (
        <div style={{ fontSize: 12.5, color: '#B42318', background: '#FCEBEB', borderTop: '1px solid #F3B9B9', padding: '8px 13px', lineHeight: 1.8 }}>
          {d.overdue.map((o, i) => <div key={i}><i className="ti ti-alert-triangle" aria-hidden /> {o}</div>)}
        </div>
      )}
      {!!d.notes.length && (
        <div style={{ fontSize: 12, color: '#92400E', background: '#FFFBEB', borderTop: '1px solid #F1C232', padding: '8px 13px', lineHeight: 1.7 }}>
          {d.notes.map((n, i) => <div key={i}><i className="ti ti-info-circle" aria-hidden /> {n}</div>)}
        </div>
      )}
      {review && !!review.unbacked?.length && (
        <div style={{ fontSize: 12, color: '#B42318', background: '#FCEBEB', borderTop: '1px solid #F3B9B9', padding: '8px 13px' }}>
          <i className="ti ti-file-off" aria-hidden /> {t('pa_compliance_unbacked', { n: review.unbacked.length })}
        </div>
      )}
      {msg && <div style={{ fontSize: 12.5, color: '#166534', background: '#E8F6EE', borderTop: '1px solid #BBE5CB', padding: '8px 13px' }}>{msg}</div>}
      {err && <div style={{ fontSize: 12.5, color: '#B42318', background: '#FCEBEB', borderTop: '1px solid #F3B9B9', padding: '8px 13px' }}>{err}</div>}

      <div style={{ borderTop: '1px solid var(--border)', padding: '9px 13px', fontSize: 11.5, color: 'var(--text-secondary)', lineHeight: 1.9 }}>
        <div>{t('pa_compliance_agm_due', { date: dmy(d.agmDueBy) })}{d.agmDate ? ` · ${t('pa_compliance_agm_held', { date: dmy(d.agmDate) })}` : ` · ${t('pa_compliance_agm_not_held')}`}
          {d.statementsToShareholdersBy ? ` · ${t('pa_compliance_statements_due', { date: dmy(d.statementsToShareholdersBy) })}` : ''}</div>
        {d.shareholderListDueBy && <div>{t('pa_compliance_bojo5_due', { date: dmy(d.shareholderListDueBy) })}</div>}
        {!!d.filed.length && <div>{t('pa_compliance_filed_label')} {d.filed.map(f => `${KIND_TH[f.kind] || f.kind} ${dmy(f.filedOn)}${f.receiptNo ? ` (${f.receiptNo})` : ''}`).join(' · ')}</div>}
        {/* s.19: who keeps these books. The first question an inspector asks. */}
        <div>
          {t('pa_compliance_bookkeeper_label')} {live.length
            ? live.map(a => `${a.name}${a.tfac_no ? ` ${t('pa_compliance_tfac', { no: a.tfac_no })}` : ''}${a.tfac_status === 'lapsed' ? ` — ${t('pa_compliance_tfac_lapsed')}` : ''}`).join(', ')
            : <span style={{ color: '#B45309', fontWeight: 700 }}>{t('pa_compliance_no_bookkeeper')}</span>}
        </div>
        {live.filter(a => !a.notified_dbd_on).map(a => (
          <div key={a.id} style={{ color: '#92400E' }}>
            <i className="ti ti-bell-exclamation" aria-hidden /> {t('pa_compliance_eacct_pending', { name: a.name, date: dmy(a.from_date) })}
            <button onClick={() => notify(a, 'start')} disabled={!!busy} style={{ ...btn, padding: '2px 8px', fontSize: 11, marginLeft: 7 }}>{t('pa_compliance_notified_today')}</button>
          </div>
        ))}
      </div>

      {mode === 'audit' && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '11px 13px 13px' }}>
          <div style={row}>
            <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_compliance_f_agm_date')}<br />
              <input type="date" value={af.agmDate} onChange={e => setAf({ ...af, agmDate: e.target.value })} style={{ ...inp, width: 150 }} /></label>
            <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_compliance_f_statements_sent')}<br />
              <input type="date" value={af.statementsSentOn} onChange={e => setAf({ ...af, statementsSentOn: e.target.value })} style={{ ...inp, width: 150 }} /></label>
            <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_compliance_f_audit_completed')}<br />
              <input type="date" value={af.auditCompletedOn} onChange={e => setAf({ ...af, auditCompletedOn: e.target.value })} style={{ ...inp, width: 150 }} /></label>
            <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_compliance_f_opinion')}<br />
              <select value={af.auditOpinion} onChange={e => setAf({ ...af, auditOpinion: e.target.value })} style={{ ...inp, width: 170 }}>
                <option value="">—</option>
                <option value="unqualified">{t('pa_compliance_opinion_unqualified')}</option>
                <option value="qualified">{t('pa_compliance_opinion_qualified')}</option>
                <option value="adverse">{t('pa_compliance_opinion_adverse')}</option>
                <option value="disclaimer">{t('pa_compliance_opinion_disclaimer')}</option>
              </select></label>
          </div>
          <div style={row}>
            <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_compliance_f_auditor')}<br />
              <input value={af.auditorName} onChange={e => setAf({ ...af, auditorName: e.target.value })} style={{ ...inp, width: 200 }} /></label>
            <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_compliance_f_auditor_license')}<br />
              <input value={af.auditorLicense} onChange={e => setAf({ ...af, auditorLicense: e.target.value })} style={{ ...inp, width: 120 }} /></label>
            {/* s.3 sattha: a CPA may certify any company; a Tax Auditor only some. Recorded, not assumed. */}
            <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_compliance_f_auditor_kind')}<br />
              <select value={af.auditorKind} onChange={e => setAf({ ...af, auditorKind: e.target.value })} style={{ ...inp, width: 190 }}>
                <option value="">—</option>
                <option value="cpa">{t('pa_compliance_auditor_cpa')}</option>
                <option value="tax_auditor">{t('pa_compliance_auditor_ta')}</option>
              </select></label>
            <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_compliance_f_audit_fee')}<br />
              <input value={af.auditFee} onChange={e => setAf({ ...af, auditFee: e.target.value })} inputMode="decimal" style={{ ...inp, width: 120, textAlign: 'right' }} /></label>
            <button onClick={saveAudit} disabled={!!busy} style={{ ...btn, background: 'var(--accent)', color: '#fff', border: 'none' }}>{t('pa_compliance_save')}</button>
          </div>
        </div>
      )}

      {mode === 'file' && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '11px 13px 13px', ...row, marginBottom: 0 }}>
          <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_compliance_f_kind')}<br />
            <select value={fl.kind} onChange={e => setFl({ ...fl, kind: e.target.value })} style={{ ...inp, width: 200 }}>
              <option value="financial_statements">{t('pa_compliance_kind_fs')} · {t('pa_compliance_kind_fs_gloss')}</option>
              <option value="shareholder_list">{t('pa_compliance_kind_bojo5')} · {t('pa_compliance_kind_bojo5_gloss')}</option>
            </select></label>
          <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_compliance_f_filed_on')}<br />
            <input type="date" value={fl.filedOn} onChange={e => setFl({ ...fl, filedOn: e.target.value })} style={{ ...inp, width: 150 }} /></label>
          <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_compliance_f_receipt_no')}<br />
            <input value={fl.receiptNo} onChange={e => setFl({ ...fl, receiptNo: e.target.value })} style={{ ...inp, width: 160 }} /></label>
          <button onClick={file} disabled={!!busy || !fl.filedOn} style={{ ...btn, background: 'var(--accent)', color: '#fff', border: 'none' }}>{t('pa_compliance_record')}</button>
        </div>
      )}
    </div>
  );
}
